import { DIALOG_DATA, DialogRef } from '@angular/cdk/dialog';
import { ChangeDetectionStrategy, Component, DestroyRef, inject, Injector, OnInit } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { DefaultLocale } from '@app/+authenticated/shared/locale/locale.helper';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { mgcIconPen2Regular } from '@sb/mgc-icons/mgcIcon-pen_2_regular.icon';
import { featureFlag, FeatureFlagPipe, FeatureService } from '@sb/shared/feature';
import { ButtonComponent, IconButtonComponent } from '@sb/ui/button';
import { CardModule } from '@sb/ui/card';
import { CheckboxComponent } from '@sb/ui/checkbox';
import { SbDialogModule, SbDialogService } from '@sb/ui/dialog';
import { SbFormFieldComponent } from '@sb/ui/form-field';
import { IconComponent, IconRegistry } from '@sb/ui/icon';
import { NoticeComponent } from '@sb/ui/notice';
import { SearchInputComponent } from '@sb/ui/search-input';
import { SelectComponent, SelectItem } from '@sb/ui/select';
import { ToastService } from '@sb/ui/toast';
import { UploadComponent, type FileValidationResult } from '@sb/ui/upload';
import { finalize, switchMap } from 'rxjs';

import {
  FileUploadResult,
  GroupedVariables,
  TemplateCategory,
  TemplateValidationResult,
  TemplateVariable,
  toTranslatedESignField,
  type ESignField,
} from '../../models/contract-template.model';
import { ContractTemplateService } from '../../services/contract-template.service';
import { CreateNewFieldModalComponent } from '../create-new-field-modal/create-new-field-modal.component';
import { FieldsButtonComponent } from '../fields-button/fields-button.component';
import { PreviewTemplateContractModalComponent } from '../preview-template-contract-modal/preview-template-contract-modal.component';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'upload-template-contract-modal',
  imports: [
    SbDialogModule,
    TranslatePipe,
    SearchInputComponent,
    CardModule,
    FieldsButtonComponent,
    UploadComponent,
    FormsModule,
    ButtonComponent,
    IconButtonComponent,
    IconComponent,
    ReactiveFormsModule,
    SelectComponent,
    SbFormFieldComponent,
    CheckboxComponent,
    FeatureFlagPipe,
    NoticeComponent,
  ],
  templateUrl: './upload-template-contract-modal.component.html',
  styleUrl: './upload-template-contract-modal.component.scss',
})
export class UploadTemplateContractModalComponent implements OnInit {
  translateService = inject(TranslateService);
  toast = inject(ToastService);
  contractTemplateService = inject(ContractTemplateService);
  #destroyRef = inject(DestroyRef);
  #dialogService = inject(SbDialogService);
  #dialogRef = inject(DialogRef<boolean>);
  #injector = inject(Injector);
  data = inject<{ category?: TemplateCategory }>(DIALOG_DATA, { optional: true });

  TemplateCategory = TemplateCategory;

  constructor() {
    inject(IconRegistry).registerIcons([mgcIconPen2Regular]);
  }

  uploading = false;
  searchQuery: string;
  requiresGuardianSignature = new FormControl<boolean>(false, { nonNullable: true });
  guardianSignatureCount = new FormControl<number>(0, { nonNullable: true });
  features = featureFlag;

  #countFeatureEnabled = inject(FeatureService).isEnabled(featureFlag.TMP_GUARDIAN_SIGNATURE_COUNT);

  // Count flag on → a single 0/1/2 "Number of guardians" selector; off → the legacy checkbox.
  guardianSignatureCountItems: SelectItem<number>[] = [
    { value: 0, text: this.translateService.instant('No guardians') },
    { value: 1, text: this.translateService.instant('1 guardian') },
    { value: 2, text: this.translateService.instant('2 guardians') },
  ];

  #documentsExampleByLocale: Record<string, string> = {
    'en-GB':
      'https://assets.shiftbase.com/hubfs/application/Shiftbase_Example_Contract_with_variables_August_2025_EN.docx',
    'nl-NL':
      'https://assets.shiftbase.com/hubfs/application/Shiftbase_Example_Contract_with_variables_August_2025_NL.docx',
    'de-DE':
      'https://assets.shiftbase.com/hubfs/application/Shiftbase_Example_Contract_with_variables_August_2025_DE.docx',
  };

  exampleDocumentUrl =
    this.#documentsExampleByLocale[this.translateService.getCurrentLang() ?? DefaultLocale] ??
    this.#documentsExampleByLocale[DefaultLocale];

  systemVariables: TemplateVariable[];
  groupedSystemVariables: GroupedVariables[];

  eSignFields: ESignField[] = [
    'signing_date_employee',
    'signature_employee',
    'signing_date_employer',
    'signature_employer',
  ].map((placeholderName) => toTranslatedESignField(placeholderName, this.translateService));

  // Guardian e-sign anchors per guardian (#1 then #2): signing date + signature. Appended to
  // the "E-Sign fields" card for the selected number of guardians.
  #guardianESignFieldsByOrdinal: ESignField[][] = [
    ['signing_date_guardian', 'signature_guardian'],
    ['signing_date_guardian_2', 'signature_guardian_2'],
  ].map((guardianPlaceholders) =>
    guardianPlaceholders.map((placeholderName) => toTranslatedESignField(placeholderName, this.translateService)),
  );

  // Guardian name placeholders per guardian (#1 then #2). Shown in the "Guardian fields"
  // card (below the e-sign fields) for the selected number of guardians.
  #guardianNameFieldsByOrdinal: Array<Array<Pick<TemplateVariable, 'type' | 'label' | 'variable'>>> = [
    [
      { type: 'text', label: this.translateService.instant('Guardian First Name'), variable: 'guardian_first_name' },
      { type: 'text', label: this.translateService.instant('Guardian Prefix'), variable: 'guardian_prefix' },
      { type: 'text', label: this.translateService.instant('Guardian Last Name'), variable: 'guardian_last_name' },
      { type: 'text', label: this.translateService.instant('Guardian Full Name'), variable: 'guardian_full_name' },
    ],
    [
      {
        type: 'text',
        label: this.translateService.instant('Guardian 2 First Name'),
        variable: 'guardian_2_first_name',
      },
      { type: 'text', label: this.translateService.instant('Guardian 2 Prefix'), variable: 'guardian_2_prefix' },
      { type: 'text', label: this.translateService.instant('Guardian 2 Last Name'), variable: 'guardian_2_last_name' },
      { type: 'text', label: this.translateService.instant('Guardian 2 Full Name'), variable: 'guardian_2_full_name' },
    ],
  ];

  /**
   * Count sent to the backend. With the count feature on, the 0/1/2 selector is authoritative;
   * otherwise it falls back to the legacy single-guardian checkbox (0 or 1).
   */
  #effectiveGuardianCount(): number {
    if (this.#countFeatureEnabled) {
      return this.guardianSignatureCount.value;
    }

    return this.requiresGuardianSignature.value ? 1 : 0;
  }

  guardianFieldsRequired(): boolean {
    return this.#effectiveGuardianCount() > 0;
  }

  guardianESignFields(): ESignField[] {
    return this.#guardianESignFieldsByOrdinal.slice(0, this.#effectiveGuardianCount()).flat();
  }

  guardianNameFields(): Array<Pick<TemplateVariable, 'type' | 'label' | 'variable'>> {
    return this.#guardianNameFieldsByOrdinal.slice(0, this.#effectiveGuardianCount()).flat();
  }

  ngOnInit() {
    void this.#getSystemVariables().subscribe((variables) => {
      this.systemVariables = variables;
      this.#setDisplayedSystemVariables(variables);
    });

    // Listen for template updates and refresh the system variables
    this.contractTemplateService.templatesUpdated$.pipe(takeUntilDestroyed(this.#destroyRef)).subscribe(() => {
      this.#refreshSystemVariables();
    });
  }

  createNewField() {
    this.#dialogService.setSize('sm').open(CreateNewFieldModalComponent, { injector: this.#injector });
  }

  editField(field: TemplateVariable) {
    this.#dialogService.setSize('sm').open(CreateNewFieldModalComponent, { data: { field }, injector: this.#injector });
  }

  onSearch(): void {
    if (this.searchQuery) {
      const filteredVariables = this.systemVariables.filter((variable) =>
        variable.label.toLowerCase().includes(this.searchQuery.toLowerCase()),
      );
      this.#setDisplayedSystemVariables(filteredVariables);
    } else {
      this.#setDisplayedSystemVariables(this.systemVariables);
    }
  }

  handleFileInput({ validFiles, errors }: FileValidationResult) {
    const [file] = validFiles;
    const allowedExtensions = ['.doc', '.docx'];

    const fileName = file?.name.toLowerCase() ?? '';
    const fileExtensionValid = allowedExtensions.some((ext) => fileName.endsWith(ext));

    if (!file || errors.invalidFileFound || !fileExtensionValid) {
      this.toast.openDanger(this.translateService.instant('Only .docx files are supported.'));
      return;
    }

    this.uploading = true;
    void this.contractTemplateService
      .uploadTemplateContract(file)
      .pipe(
        switchMap((response: FileUploadResult) =>
          this.contractTemplateService
            .confirmUploadedTemplateContract(
              fileName,
              response.resource_id,
              this.data?.category,
              this.#effectiveGuardianCount(),
            )
            .pipe(finalize(() => (this.uploading = false))),
        ),
        takeUntilDestroyed(this.#destroyRef),
      )
      .subscribe((response: TemplateValidationResult) => {
        this.#dialogService.setSize('fullscreen').open(PreviewTemplateContractModalComponent, {
          data: { templateId: response.id, category: this.data?.category },
          injector: this.#injector,
        });
        this.close();
      });
  }

  close() {
    this.#dialogRef.close();
  }

  #refreshSystemVariables() {
    void this.#getSystemVariables().subscribe((variables) => {
      this.systemVariables = variables;
      this.#setDisplayedSystemVariables(variables);
      // Re-apply search filter if there's an active search
      if (this.searchQuery) {
        this.onSearch();
      }
    });
  }

  #getSystemVariables() {
    return this.contractTemplateService.getSystemVariables();
  }

  #setDisplayedSystemVariables(variables: TemplateVariable[]) {
    this.groupedSystemVariables = this.#groupVariablesByCategory(variables);
  }

  #groupVariablesByCategory(variables: TemplateVariable[]): GroupedVariables[] {
    const grouped = variables.reduce(
      (acc, variable) => {
        const categoryKey = variable.category?.key || '';
        if (!acc[categoryKey]) {
          acc[categoryKey] = {
            label: variable.category?.label || '',
            fields: [],
          };
        }
        acc[categoryKey].fields.push(variable);
        return acc;
      },
      {} as Record<string, GroupedVariables>,
    );

    return Object.values(grouped);
  }
}
